import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, FC } from "react";
import {
  MdHolidayVillage,
  MdPublishedWithChanges,
  MdRecordVoiceOver,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { TactikSiteTopBar } from "../app/TactikSiteTopBar";
import { siteConfig } from "../../site-config/SiteConfig";
import "./StandaloneHomePage.css";

const PHILOSOPHY_CARDS = [
  {
    image: "/standonletactik/philosophy/stock_recommend.png",
    title: "我们不推荐个股",
    desc: "个股推荐弊端多，用户越多，越可能遭遇反向收割。投资在于逻辑，而非代码。",
  },
  {
    image: "/standonletactik/philosophy/top_strategy.png",
    title: "不存在绝对定律",
    desc: "投资是一个混沌系统，股价不可预测，任何内幕消息、顶流战法都是营销噱头。",
  },
  {
    image: "/standonletactik/philosophy/trade_system.png",
    title: "智者培养交易体系",
    desc: "从机构、游资到牛散，他们都在培养自己的交易体系，只赚认知内的收益。",
  },
  {
    image: "/standonletactik/philosophy/software.png",
    title: "好记性不如好系统",
    desc: "记录每一次决策、跟踪每一次结果，让经验不再遗忘，让错误不再重复。",
  },
] as const;

/**
 * 首页产品区 · 布局尺寸配置
 * 修改此处即可，无需改 CSS。
 * rowMaxWidth 为图文行总宽上限；imageContainerWidth / descContainerWidth 分别绑定图片区、文案区（与左右位置无关）。
 */
const PRODUCT_LAYOUT = {
  /** 每行（图片 + 介绍）最大宽度 */
  rowMaxWidth: "1200px",
  /** 图片区（Mac 窗口）宽度 */
  imageContainerWidth: "750px",
  /** 文案区宽度 */
  descContainerWidth: "400px",
  /** 文案区内边距（左右） */
  descPaddingX: "50px",
  /** 截图高度：auto 随图片比例；或固定如 360px */
  imageHeight: "500px",
} as const;

const productLayoutStyle = {
  "--sh-product-row-max-width": PRODUCT_LAYOUT.rowMaxWidth,
  "--sh-product-image-container-width": PRODUCT_LAYOUT.imageContainerWidth,
  "--sh-product-desc-container-width": PRODUCT_LAYOUT.descContainerWidth,
  "--sh-product-desc-padding-x": PRODUCT_LAYOUT.descPaddingX,
  "--sh-product-image-height": PRODUCT_LAYOUT.imageHeight,
} as CSSProperties;

/** 产品简介分段：desc 中用 \\n 分隔段落 */
function productDescParagraphs(desc: string): string[] {
  return desc
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

const PRODUCT_ITEMS = [
  {
    name: "情报局",
    desc: "当所有信息都在呐喊时，洞察反而变得稀缺。情报局在全球海量信息流中，持续筛选那些真正可能影响市场、产业与企业价值的重要事件。拒绝无意义的资讯堆砌，用更少的信息，获得更深的认知；用更高质量的情报，辅助更理性的投资决策。\n 我们相信，在信息过剩的时代，筛选能力比获取能力更重要。很快我们的AI还会针对每个情报给出解读，解读仅供参考，不能作为交易依据。",
    image: "/standonletactik/products/intel.png",
  },
  {
    name: "策略市场",
    desc: "不是每一次技术形态发生时，股票都会上扬而去；但反过来大概率是成立的，也就是每次股票启动前会产生一些技术信号，策略市场提供多样化的信号发现策略，策略会对每个股票每日的K线打分，由您自己设置信号触发的阈值。\n 我们不建议直接使用策略信号指导交易，您需要结合标的资金面、基本面、消息面等多方面综合判断。",
    image: "/standonletactik/products/strategy.png",
  },
  {
    name: "机构在看",
    desc: "机构（公私募基金、游资、金融机构等）往往代表了资金，机构关注的热点代表了资金可能前往的方向；由于很多热点会持续发酵，有时候那些热点的标的已经处在高位了；\n 所以对于一些新出现的热点，我们会着重标出，可以重点关注；当然持续发酵的热点，也未必就是见顶的信号，这些都需要你来进一步分析和判断。",
    image: "/standonletactik/products/institution.png",
  },
  {
    name: "概念库",
    desc: "人工记忆5000+只股票和数百个概念是不现实的，概念库可以帮助我们快速筛选出符合多概念的标的，并且支持按照多个维度进行排序，包括：基本面分、位置分、资金流入等；\n 比如位置分可以帮我们快速找到多个概念下目前处于低位的标的；我们还将开放多维度综合排序，比如您将可以同时找出基本面好、位置相对较低的标的。",
    image: "/standonletactik/products/concept.png",
  },
  {
    name: "用户持仓",
    desc: "如果您不对自己的操作进行复盘，那即便投资数年，您的交易历史都不会给您带来太多的经验沉淀，您仍处在一种迷茫、没有体系的状态，盈亏都靠运气。\n 我们鼓励用户及时的登记自己的持仓，并在Tactik管理自己的自选股，刚开始可能有些繁琐，但养成习惯后您会爱上它，您将会实实在在的感受到自己投资能力的成长。",
    image: "/standonletactik/products/holdings.png",
  },
] as const;

type CocreateIconProps = { size?: number };
type CocreateIcon = FC<CocreateIconProps>;

const CocreateFeatureIcon: FC<
  CocreateIconProps & { icon: typeof MdRecordVoiceOver }
> = ({ icon, ...props }) => {
  const Icon = icon as CocreateIcon;
  return <Icon {...props} />;
};

const CO_CREATE_ICON_PROPS: CocreateIconProps = { size: 34 };

const CO_CREATE_SECTION = {
  title: "与你共创，共建投资家园",
  subline:
    "紧跟时代变化，持续推出更好用的投资工具。你的建议，是我们进化的方向。",
  pillars: [
    {
      icon: MdRecordVoiceOver,
      label: "倾听与采集",
      desc: "认真听取每一位用户的建议，把真实需求收进产品规划。",
    },
    {
      icon: MdPublishedWithChanges,
      label: "持续迭代",
      desc: "小步快跑、持续交付，让工具能力始终跟得上时代。",
    },
    {
      icon: MdHolidayVillage,
      label: "构建家园",
      desc: "这里是你长期使用的投资家园，我们一起把它建好、用好。",
    },
  ],
} as const;

const StandaloneHomePage = observer(() => {
  const secondScreenRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSpaceHome = useCallback(() => {
    navigate("/tactik/intel");
  }, [navigate]);

  const handleScrollToSecondScreen = useCallback(() => {
    secondScreenRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const brandName = siteConfig.shellLogoAlt;

  return (
    <div className="standalone-home">
      <TactikSiteTopBar variant="hero" heroScrolled={navScrolled} />

      <section className="standalone-home-first" aria-label="首页首屏">
        <div className="standalone-home-logo">
          <img
            src={siteConfig.shellLogo}
            alt={brandName}
            draggable={false}
          />
        </div>
        <h1 className="standalone-home-welcome">{siteConfig.welcomeWords}</h1>
        <p className="standalone-home-intro">{siteConfig.appIntroduction}</p>
       
        <div className="standalone-home-actions">
          <button
            type="button"
            className="standalone-home-cta"
            onClick={goSpaceHome}
          >
            立即开始使用
          </button>
          <button
            type="button"
            className="standalone-home-cta standalone-home-cta--secondary"
            onClick={handleScrollToSecondScreen}
          >
            我们的理念
          </button>
        </div>
        <button
          type="button"
          className="standalone-home-arrow-down"
          aria-label="向下滚动"
          onClick={handleScrollToSecondScreen}
        />
      </section>

      <main className="standalone-home-scroll" aria-label="产品介绍"> 

        <section
          ref={secondScreenRef}
          className="sh-panel sh-panel--gray"
          id="philosophy"
        >
          <div className="sh-panel-inner standalone-home-second">
            <header className="sh-philosophy-head">
              <h2 className="sh-headline">授人以渔，方能长久</h2>
              <p className="sh-subline">
                不卖课、不荐股、不吹嘘。只把决策所需的信息，放在你手边。
              </p>
            </header>
            <div className="sh-philosophy-cards">
              {PHILOSOPHY_CARDS.map((card) => (
                <article className="sh-idea-card" key={card.title}>
                  <div className="sh-idea-card-media">
                    <img
                      className="sh-idea-card-image"
                      src={card.image}
                      alt={card.title}
                      draggable={false}
                    />
                  </div>
                  <h3 className="sh-idea-card-title">{card.title}</h3>
                  <p className="sh-idea-card-desc">{card.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sh-panel sh-panel--products" id="products">
          <div className="sh-panel-inner standalone-home-second">
            <div className="sh-products-head">
              <h2 className="sh-headline">五大产品构建智能座舱</h2>
              <p className="sh-subline">
              围绕 基本面 · 消息面 · 资金面 构建实用的投资决策系统
              </p>
              <hr className="sh-products-head-divider" aria-hidden="true" />
            </div>
          </div>
          <div className="sh-products-showcase" style={productLayoutStyle}>
            {PRODUCT_ITEMS.map(({ name, desc, image }, index) => (
              <article
                className={`sh-product-bg ${
                  index % 2 === 0
                    ? "sh-product-bg--light"
                    : "sh-product-bg--dark"
                }`}
                key={name}
              >
                <div
                  className={`sh-product-row${
                    index % 2 === 1 ? " sh-product-row--reverse" : ""
                  }`}
                >
                  <div className="sh-product-shot">
                    <div className="sh-mac-window">
                      <div className="sh-mac-window-titlebar">
                        <span
                          className="sh-mac-dot sh-mac-dot--close"
                          aria-hidden="true"
                        />
                        <span
                          className="sh-mac-dot sh-mac-dot--minimize"
                          aria-hidden="true"
                        />
                        <span
                          className="sh-mac-dot sh-mac-dot--maximize"
                          aria-hidden="true"
                        />
                        <span className="sh-mac-window-title">{name}</span>
                      </div>
                      <div className="sh-mac-window-content">
                        <img
                          src={image}
                          alt={`${name} 产品截图`}
                          draggable={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="sh-product-copy">
                    <h3 className="sh-product-name">
                      <span className="sh-product-index" aria-hidden="true">
                        {index + 1}
                      </span>
                      {name}
                    </h3>
                    <div className="sh-product-desc-block">
                      {productDescParagraphs(desc).map((para, paraIndex) => (
                        <p key={paraIndex} className="sh-product-desc">
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
            <article className="sh-cocreate-bg" aria-label={CO_CREATE_SECTION.title}>
              <div className="sh-cocreate-inner">
                <header className="sh-cocreate-head">
                  <h2 className="sh-headline">{CO_CREATE_SECTION.title}</h2>
                  <p className="sh-subline">{CO_CREATE_SECTION.subline}</p>
                </header>
                <ul className="sh-cocreate-features">
                  {CO_CREATE_SECTION.pillars.map(({ icon, label, desc }) => (
                    <li className="sh-cocreate-feature" key={label}>
                      <span className="sh-cocreate-feature-icon" aria-hidden="true">
                        <CocreateFeatureIcon icon={icon} {...CO_CREATE_ICON_PROPS} />
                      </span>
                      <h3 className="sh-cocreate-feature-label">{label}</h3>
                      <p className="sh-cocreate-feature-desc">{desc}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </section>
      </main>
 
    </div>
  );
});

export default StandaloneHomePage;
